import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import Pagination from "@/shared/components/ui/Pagination/Pagination";
import { sortData } from "@/shared/utils/sortUtils";

const cn = (...inputs) => twMerge(clsx(inputs));

const SCROLL_THRESHOLD = 100;
const SCROLL_THROTTLE_MS = 300;
const SELECTION_COLUMN_WIDTH = "40px";
const PINNED_COLUMN_SHADOW = "shadow-[2px_1px_5px_-6px_rgba(15,23,42,0.45)]";
export const TABLE_SCROLL_HEIGHT = "min(550px, calc(100dvh - 145px))";

const getColumnWidthStyle = (col) =>
  col.width
    ? { width: col.width, minWidth: col.width, maxWidth: col.width }
    : {};

export default function CustomTable({
  data = [],
  columns = [],
  height = TABLE_SCROLL_HEIGHT,
  renderCell,
  enableSelection = false,
  rowsPerPage: initialRowsPerPage = 15,
  setRowsPerPage,
  page = 1,
  totalCount,
  isServerSide,
  onScrollEnd,
  isLoadingMore = false,
  /** Label for the row shown under the data while isLoadingMore is on. */
  loadingMoreText = "Loading more...",
  onSelectionChange,
  selectedItems,
  setSelectedItems: _setSelectedItems,
  pl,
  pr,
  /** Background (and anything the sticky header row needs to paint); must be opaque. */
  headerColor,
  /** Extra classes for every header cell: text size, color, weight, padding, border. Wins over the defaults. */
  headerClassName,
  /** Extra classes for the header label itself (inside the cell). Wins over the defaults. */
  headerLabelClassName,
  onRowClick,
  disableRowClickKeys = ["actions"],
  onPageChange,
  groupBy = null,
  groupByFallbackLabel = "(No group)",
  groupSortCompare = undefined,
  tableLayout = "fixed",
  /** Fit the table to its container (no horizontal scroll): column `width`s become shares of
   *  the available width and long cell content must truncate. Fixed layout only takes effect
   *  on a table with a set width, which the default `min-w-full` alone does not give. */
  fitToWidth = false,
  pinFirstColumn = false,
  showCellBorders = false,
}) {
  const [internalSelectedKeys, setInternalSelectedKeys] = useState(new Set([]));
  const [collapsed, setCollapsed] = useState(new Set());
  const [sortDescriptor, setSortDescriptor] = useState({});
  const [dynamicRowsPerPage, setDynamicRowsPerPage] =
    useState(initialRowsPerPage);
  const tableRef = useRef();
  const tableBodyRef = useRef();
  const headerSelectAllInputRef = useRef(null);
  const headerBgClass = headerColor || "bg-gray-100 dark:bg-gray-900";
  const pinnedNameColumnLeft = enableSelection ? SELECTION_COLUMN_WIDTH : "0px";
  const cellBorderClass = showCellBorders
    ? "border-r border-b border-gray-100 dark:border-gray-800 last:border-r-0"
    : "";
  const tableFrameClass = showCellBorders
    ? "rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
    : "";
  const tableBorderModeClass = showCellBorders
    ? "border-separate border-spacing-0"
    : "";

  const isControlled = selectedItems !== undefined;
  const controlledIds = useMemo(
    () => new Set((selectedItems || []).map((c) => c.id)),
    [selectedItems],
  );

  const isItemSelected = useCallback(
    (id) =>
      isControlled ? controlledIds.has(id) : internalSelectedKeys.has(id),
    [isControlled, controlledIds, internalSelectedKeys],
  );

  // notify parent when selection changes
  useEffect(() => {
    if (isControlled) return;
    if (typeof onSelectionChange !== "function") return;
    const selected = data.filter((item) => internalSelectedKeys.has(item.id));
    onSelectionChange(selected);
  }, [internalSelectedKeys, data, onSelectionChange, isControlled]);

  const isInfiniteScroll = !!onScrollEnd;

  const calculateRowsPerPage = useCallback(() => {
    if (!isInfiniteScroll) return;
    if (!tableBodyRef.current || !tableRef.current) return;
    const sampleRow = tableBodyRef.current.querySelector("tr");
    if (!sampleRow) return;

    const rowHeight = sampleRow.offsetHeight;
    if (!rowHeight) return;

    const containerHeight = tableRef.current.clientHeight;
    const headerEl = tableRef.current.querySelector("thead");
    const headerHeight = headerEl ? headerEl.offsetHeight : 0;
    const availableHeight = containerHeight - (headerHeight + 50);
    const calculatedRows = Math.ceil(availableHeight / rowHeight);

    const newRowsPerPage = Math.max(calculatedRows, 5);
    if (newRowsPerPage !== dynamicRowsPerPage) {
      setDynamicRowsPerPage(newRowsPerPage);
      if (setRowsPerPage) {
        setRowsPerPage(newRowsPerPage);
      }
    }
  }, [dynamicRowsPerPage, setRowsPerPage, isInfiniteScroll]);

  useEffect(() => {
    if (!isInfiniteScroll) return;
    const resizeObserver = new ResizeObserver(() => {
      calculateRowsPerPage();
    });
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [calculateRowsPerPage, isInfiniteScroll]);

  useEffect(() => {
    if (!isInfiniteScroll) return;
    const timer = setTimeout(() => calculateRowsPerPage(), 100);
    return () => clearTimeout(timer);
  }, [calculateRowsPerPage, data, isInfiniteScroll]);

  // Scroll table back to the top when page changes
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [page]);

  const effectiveRowsPerPage = isInfiniteScroll
    ? dynamicRowsPerPage
    : initialRowsPerPage;

  const allSortedData = useMemo(() => {
    if (!sortDescriptor.column || !sortDescriptor.direction) {
      return data;
    }
    // A column can supply `sortValue(item)` when its cell is computed rather than a
    // plain field (e.g. a full name built from first/last, or "$9.8 Million").
    const sortCol = columns.find((c) => c.uid === sortDescriptor.column);
    const getValueFn = sortCol?.sortValue
      ? (item) => (item?._isSkeleton ? null : sortCol.sortValue(item))
      : null;
    return sortData(data, sortDescriptor.column, sortDescriptor.direction, getValueFn);
  }, [data, columns, sortDescriptor.column, sortDescriptor.direction]);

  const effectiveTotalCount =
    totalCount !== undefined ? totalCount : data.length;

  const isServerSidePagination =
    isServerSide !== undefined
      ? isServerSide
      : effectiveTotalCount != null && effectiveTotalCount > data.length;

  const paginatedItems = useMemo(() => {
    if (isInfiniteScroll && effectiveTotalCount != null && effectiveTotalCount > 0) {
      return allSortedData;
    }

    if (isServerSidePagination) {
      return allSortedData;
    } else {
      const start = (page - 1) * effectiveRowsPerPage;
      const end = start + effectiveRowsPerPage;
      return allSortedData.slice(start, end);
    }
  }, [
    allSortedData,
    page,
    effectiveRowsPerPage,
    effectiveTotalCount,
    isInfiniteScroll,
    isServerSidePagination,
  ]);

  const sortedItems = paginatedItems;

  const groups = useMemo(() => {
    if (!groupBy) return null;
    const m = new Map();
    for (const item of sortedItems) {
      const key =
        (item[groupBy] && String(item[groupBy]).trim()) || groupByFallbackLabel;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(item);
    }
    const entries = [...m.entries()];
    if (typeof groupSortCompare === "function") {
      entries.sort((a, b) => groupSortCompare(a[0], b[0]));
    } else {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    }
    return entries;
  }, [sortedItems, groupBy, groupByFallbackLabel, groupSortCompare]);

  const toggleGroup = useCallback((key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const isAllPageSelected = useMemo(() => {
    return (
      sortedItems.length > 0 &&
      sortedItems.every((item) => isItemSelected(item.id))
    );
  }, [sortedItems, isItemSelected]);

  const isSomePageSelected = useMemo(() => {
    return sortedItems.some((item) => isItemSelected(item.id));
  }, [sortedItems, isItemSelected]);

  const headerCheckboxIndeterminate = isSomePageSelected && !isAllPageSelected;

  useEffect(() => {
    const el = headerSelectAllInputRef.current;
    if (el) {
      el.indeterminate = headerCheckboxIndeterminate;
    }
  }, [headerCheckboxIndeterminate, sortedItems.length]);

  const onSelectAll = useCallback(
    (e) => {
      const checked = e.target.checked;
      if (isControlled) {
        if (!onSelectionChange) return;
        if (checked) {
          const existingIds = new Set((selectedItems || []).map((c) => c.id));
          const toAdd = sortedItems.filter((item) => !existingIds.has(item.id));
          onSelectionChange([...(selectedItems || []), ...toAdd]);
        } else {
          const pageIds = new Set(sortedItems.map((item) => item.id));
          onSelectionChange(
            (selectedItems || []).filter((c) => !pageIds.has(c.id)),
          );
        }
      } else {
        const pageIds = new Set(sortedItems.map((item) => item.id));
        setInternalSelectedKeys((prev) => {
          const next = new Set(prev);
          if (checked) {
            pageIds.forEach((id) => next.add(id));
          } else {
            pageIds.forEach((id) => next.delete(id));
          }
          return next;
        });
      }
    },
    [sortedItems, isControlled, selectedItems, onSelectionChange],
  );

  const onSelectRow = useCallback(
    (id, item) => {
      if (isControlled) {
        if (!onSelectionChange) return;
        if (controlledIds.has(id)) {
          onSelectionChange((selectedItems || []).filter((c) => c.id !== id));
        } else {
          onSelectionChange([...(selectedItems || []), item]);
        }
      } else {
        setInternalSelectedKeys((prev) => {
          const updated = new Set(prev);
          if (updated.has(id)) {
            updated.delete(id);
          } else {
            updated.add(id);
          }
          return updated;
        });
      }
    },
    [isControlled, controlledIds, selectedItems, onSelectionChange],
  );

  const onToggleGroupSelection = useCallback(
    (e, groupRows) => {
      e.stopPropagation();
      if (!enableSelection) return;
      const gids = groupRows.map((r) => r.id);
      if (isControlled) {
        if (!onSelectionChange) return;
        const allSelected = gids.every((id) => controlledIds.has(id));
        if (allSelected) {
          const gidSet = new Set(gids);
          onSelectionChange(
            (selectedItems || []).filter((c) => !gidSet.has(c.id)),
          );
        } else {
          const existing = new Set((selectedItems || []).map((c) => c.id));
          const toAdd = groupRows.filter((r) => !existing.has(r.id));
          onSelectionChange([...(selectedItems || []), ...toAdd]);
        }
      } else {
        const allSelected = gids.every((id) => internalSelectedKeys.has(id));
        setInternalSelectedKeys((prev) => {
          const next = new Set(prev);
          if (allSelected) {
            gids.forEach((id) => next.delete(id));
          } else {
            gids.forEach((id) => next.add(id));
          }
          return next;
        });
      }
    },
    [
      enableSelection,
      isControlled,
      onSelectionChange,
      selectedItems,
      controlledIds,
      internalSelectedKeys,
    ],
  );

  const lastScrollTriggerRef = useRef(0);

  const handleScroll = useCallback(() => {
    const el = tableRef.current;
    if (!el || !onScrollEnd || isLoadingMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) {
      const now = Date.now();
      if (now - lastScrollTriggerRef.current < SCROLL_THROTTLE_MS) return;
      lastScrollTriggerRef.current = now;
      onScrollEnd();
    }
  }, [onScrollEnd, isLoadingMore]);

  useEffect(() => {
    const el = tableRef.current;
    if (el && onScrollEnd) {
      el.addEventListener("scroll", handleScroll);
      return () => el.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll, onScrollEnd, data?.length]);

  useEffect(() => {
    if (
      onScrollEnd &&
      !isLoadingMore &&
      data.length > 0 &&
      data.length < totalCount &&
      sortedItems.length <= dynamicRowsPerPage
    ) {
      onScrollEnd();
    }
  }, [
    dynamicRowsPerPage,
    sortedItems.length,
    data.length,
    totalCount,
    onScrollEnd,
    isLoadingMore,
  ]);

  const renderDataRow = (item, index, rowKeyBase = "row") => (
    <tr
      key={`${item.id || rowKeyBase}-${index}`}
      onClick={() => {
        if (typeof onRowClick === "function") {
          onRowClick(item);
        }
      }}
      className={`group hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150 ${onRowClick ? "cursor-pointer" : ""}`}
    >
      {enableSelection && (
        <td
          onClick={(e) => e.stopPropagation()}
          className={`${pl ? pl : "pl-6"} ${pr ? pr : "pr-2"} ${cellBorderClass} sticky left-0 z-20 bg-white dark:bg-gray-900 py-[0.98rem] text-center group-hover:bg-gray-50 dark:group-hover:bg-gray-800 transition-colors duration-150`}
          style={{
            width: SELECTION_COLUMN_WIDTH,
            minWidth: SELECTION_COLUMN_WIDTH,
            maxWidth: SELECTION_COLUMN_WIDTH,
          }}
        >
          <div className="flex justify-center">
            <label className="inline-flex relative">
              <input
                type="checkbox"
                className="absolute opacity-0 w-0 h-0"
                checked={isItemSelected(item.id)}
                onChange={() => onSelectRow(item.id, item)}
              />
              <span
                className={`w-4 h-4 rounded border ${isItemSelected(item.id)
                  ? "bg-secondary border-secondary"
                  : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                  } flex items-center justify-center cursor-pointer`}
              >
                {isItemSelected(item.id) && (
                  <svg
                    className="w-3 h-3 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="3"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </span>
            </label>
          </div>
        </td>
      )}
      {columns.map((col, colIndex) => {
        const isPinnedNameColumn = pinFirstColumn && colIndex === 0;
        const columnStyle = {
          ...getColumnWidthStyle(col),
          ...(isPinnedNameColumn ? { left: pinnedNameColumnLeft } : {}),
        };

        return (
          <td
            key={col.uid}
            onClick={(e) => {
              if (disableRowClickKeys.includes(col.uid)) {
                e.stopPropagation();
              }
            }}
            className={`px-4 py-3 whitespace-normal break-words text-sm text-gray-700 dark:text-gray-200 transition-colors duration-150 ${fitToWidth ? "overflow-hidden" : ""} ${col.align === "center"
              ? "text-center"
              : col.align === "right"
                ? "text-right"
                : "text-left"
              } ${cellBorderClass} ${isPinnedNameColumn
                ? `sticky z-10 bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800 ${PINNED_COLUMN_SHADOW}`
                : ""
              }`}
            style={columnStyle}
          >
            {col.linkable && col.getLink ? (
              <Link to={col.getLink(item)}>{renderCell(item, col.uid)}</Link>
            ) : col.render ? (
              col.render(item)
            ) : (
              renderCell(item, col.uid)
            )}
          </td>
        );
      })}
    </tr>
  );

  const showPagination =
    onPageChange &&
    !onScrollEnd &&
    effectiveTotalCount != null &&
    initialRowsPerPage > 0 &&
    effectiveTotalCount > initialRowsPerPage;

  return (
    <div
      className="w-full flex-1 min-h-0 flex flex-col gap-4 relative overflow-hidden"
      style={{ height }}
    >
      <div className="overflow-hidden flex-1 min-h-0">
        <div
          className={`overflow-y-auto overflow-x-auto h-full ${tableFrameClass}`}
          ref={tableRef}
          style={{ paddingBottom: showPagination ? "48px" : 0 }}
        >
          <table
            className={`${fitToWidth ? "w-full table-fixed" : `min-w-full ${tableLayout === "auto" ? "table-auto" : "table-fixed"}`} ${tableBorderModeClass}`}
          >
            <thead>
              <tr className={`pb-2 sticky top-0 ${headerBgClass} z-[9999]`}>
                {enableSelection && (
                  <th
                    className={`${pr ? pr : "pr-2"} ${pl ? pl : "pl-6"
                      } ${cellBorderClass} sticky left-0 z-[10001] py-4 text-center ${headerBgClass} border-b border-gray-100 dark:border-gray-800`}
                    style={{
                      width: SELECTION_COLUMN_WIDTH,
                      minWidth: SELECTION_COLUMN_WIDTH,
                      maxWidth: SELECTION_COLUMN_WIDTH,
                    }}
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <label className="inline-flex relative">
                        <input
                          ref={headerSelectAllInputRef}
                          type="checkbox"
                          className="absolute opacity-0 w-0 h-0"
                          checked={isAllPageSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectAll(e);
                          }}
                        />
                        <span
                          className={`w-4 h-4 rounded border ${isAllPageSelected
                            ? "bg-secondary border-secondary"
                            : headerCheckboxIndeterminate
                              ? "bg-secondary/90 border-secondary"
                              : "border-gray-400 dark:border-gray-600 bg-white dark:bg-gray-800"
                            } flex items-center justify-center cursor-pointer`}
                        >
                          {headerCheckboxIndeterminate && !isAllPageSelected ? (
                            <span className="block w-2 h-0.5 bg-white rounded" />
                          ) : isAllPageSelected ? (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : null}
                        </span>
                      </label>
                    </div>
                  </th>
                )}
                {columns.map((col, colIndex) => {
                  const isPinnedNameColumn = pinFirstColumn && colIndex === 0;
                  const columnStyle = {
                    ...getColumnWidthStyle(col),
                    ...(isPinnedNameColumn
                      ? { left: pinnedNameColumnLeft }
                      : {}),
                  };

                  return (
                    <th
                      key={col.uid}
                      className={cn(
                        "px-3 py-3 text-xs text-gray-800 dark:text-gray-200 font-normal tracking-wider",
                        col.align === "center"
                          ? "text-center"
                          : col.align === "right"
                            ? "text-right"
                            : "text-left",
                        isPinnedNameColumn &&
                        `sticky z-[10000] ${headerBgClass} ${PINNED_COLUMN_SHADOW}`,
                        cellBorderClass,
                        "border-b border-gray-100 dark:border-gray-800 tracking-normal",
                        headerClassName,
                        col.headerClassName,
                      )}
                      style={columnStyle}
                      onClick={() => {
                        if (col.sortable) {
                          setSortDescriptor((prev) => {
                            if (prev.column !== col.uid) {
                              return {
                                column: col.uid,
                                direction: "ascending",
                              };
                            }
                            if (prev.direction === "ascending") {
                              return {
                                column: col.uid,
                                direction: "descending",
                              };
                            }
                            if (prev.direction === "descending") {
                              return { column: "", direction: null };
                            }
                            return { column: col.uid, direction: "ascending" };
                          });
                        }
                      }}
                    >
                      <div
                        className={cn(
                          "flex items-center font-semibold cursor-pointer w-full",
                          col.align === "center"
                            ? "justify-center"
                            : col.align === "right"
                              ? "justify-end"
                              : "justify-start",
                          sortDescriptor.column !== col.uid && "hover:text-gray-900",
                          headerLabelClassName,
                          col.headerLabelClassName,
                        )}
                      >
                        {col.name}
                        {sortDescriptor.column === col.uid && (
                          <span>
                            {sortDescriptor.direction === "ascending" ? (
                              <ArrowUp size={10} strokeWidth={3} />
                            ) : (
                              <ArrowDown size={10} strokeWidth={3} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody
              ref={tableBodyRef}
              className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800"
            >
              {groups
                ? groups.map(([groupKey, groupRows]) => {
                  const expanded = !collapsed.has(groupKey);
                  const gAll =
                    groupRows.length > 0 &&
                    groupRows.every((r) => isItemSelected(r.id));
                  const gSome = groupRows.some((r) => isItemSelected(r.id));
                  return (
                    <React.Fragment key={groupKey}>
                      <tr
                        className="group bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 cursor-pointer"
                        onClick={() => toggleGroup(groupKey)}
                      >
                        {enableSelection && (
                          <td
                            className={`${pl ? pl : "pl-6"} ${pr ? pr : "pr-2"
                              } ${cellBorderClass} sticky left-0 z-20 bg-gray-50 dark:bg-gray-800 py-2 text-center group-hover:bg-gray-100 dark:group-hover:bg-gray-750`}
                            style={{
                              width: SELECTION_COLUMN_WIDTH,
                              minWidth: SELECTION_COLUMN_WIDTH,
                              maxWidth: SELECTION_COLUMN_WIDTH,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex justify-center items-center gap-1">
                              <label className="inline-flex relative">
                                <input
                                  type="checkbox"
                                  className="absolute opacity-0 w-0 h-0"
                                  checked={gAll && groupRows.length > 0}
                                  onChange={(e) =>
                                    onToggleGroupSelection(e, groupRows)
                                  }
                                />
                                <span
                                  className={`w-4 h-4 rounded border ${gAll
                                    ? "bg-secondary border-secondary"
                                    : gSome
                                      ? "bg-secondary/90 border-secondary"
                                      : "border-gray-400 dark:border-gray-600 bg-white dark:bg-gray-800"
                                    } flex items-center justify-center cursor-pointer`}
                                >
                                  {gSome && !gAll ? (
                                    <span className="block w-2 h-0.5 bg-white rounded" />
                                  ) : gAll ? (
                                    <svg
                                      className="w-3 h-3 text-white"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="3"
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  ) : null}
                                </span>
                              </label>
                            </div>
                          </td>
                        )}
                        <td
                          colSpan={columns.length}
                          className={`${cellBorderClass} px-2 py-2 text-left`}
                        >
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                            {expanded ? (
                              <ChevronDown size={18} className="shrink-0 text-gray-600 dark:text-gray-400" />
                            ) : (
                              <ChevronRight size={18} className="shrink-0 text-gray-600 dark:text-gray-400" />
                            )}
                            <span>{groupKey}</span>
                            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                              ({groupRows.length})
                            </span>
                          </div>
                        </td>
                      </tr>
                      {expanded &&
                        groupRows.map((item, idx) =>
                          renderDataRow(item, idx, groupKey),
                        )}
                    </React.Fragment>
                  );
                })
                : sortedItems.map((item, index) =>
                  renderDataRow(item, index, "row"),
                )}
              {isLoadingMore && (
                <tr className="bg-white dark:bg-gray-900">
                  <td
                    colSpan={columns.length + (enableSelection ? 1 : 0)}
                    className="px-4 py-6 text-center"
                  >
                    <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                      <Loader2 size={18} className="animate-spin" />
                      <span>{loadingMoreText}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showPagination && (
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-20 flex justify-center bg-gradient-to-t from-white dark:from-gray-900 from-75% to-transparent pt-6">
          <div className="pointer-events-auto w-full flex justify-center">
            <Pagination
              currentPage={page}
              totalCount={effectiveTotalCount}
              rowsPerPage={initialRowsPerPage}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
